import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { useWindowResize } from '../../../../hooks/useWindowResize';
import { FieldValue } from '../../../datasets/instances/store';
import { DatasetFieldType, SingleDatasetFieldType } from '../../../datasets/store';
import { Button } from '../../../shared/button/Button';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import { LoadingDots } from '../../../shared/loading-dots/LoadingDots';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { getAllDocumentNames } from '../../documents/store';
import styles from './AIManager.module.scss';
import { AIManagerPieChart } from './AIManagerPieChart';
import { AIQueryItem } from './AIQueryItem';
import { DefaultValue } from './DefaultValue';
import { HistoricalAIQueriesTimeline } from './HistoricalAIQueriesTimeline';
import { HistoricalAIQueryReadOnly } from './HistoricalAIQueryReadOnly';
import {
    DocumentQuery,
    getAgreementTypeSystemInfo,
    getCurrentDocumentQueries,
    getDocumentQueryHistory,
    getDocumentQueryHistoryLoading,
    getDocumentQuerySaveDisabled,
    getSelectedAIAgreementTypeId,
    getSelectedHistoricalDocumentQueryId,
    PortfolioTotal,
    saveUpdatedQueriesStarted,
    resetHistoricalQueryData,
    setSelectedHistoricalDocumentId,
    SystemIdInfoUI,
    updateDocumentQuery,
    updateQuerySystemInfo
} from './store';

const defaultSystemIdInfo: SystemIdInfoUI = {
    dropdownListId: null,
    isMultiSelect: false,
    type: null,
    systemId: ''
};

export const HistoricalAIQueries: React.FC = () => {
    const [editing, setEditing] = useState(false);

    const dispatch = useAppDispatch();
    const currentQueries = useAppSelector(getCurrentDocumentQueries);
    const selectedAgreementTypeId = useAppSelector(getSelectedAIAgreementTypeId);
    const documentQueryHistory = useAppSelector(getDocumentQueryHistory);
    const documentNames = useAppSelector(getAllDocumentNames);
    const loading = useAppSelector(getDocumentQueryHistoryLoading);
    const selectedHistoricalDocumentQueryId = useAppSelector(getSelectedHistoricalDocumentQueryId);
    const relevantDocumentNames = useMemo(() => documentNames.filter(({ agreementTypeId }) => agreementTypeId === selectedAgreementTypeId).map(({ documentName, documentNameId }) => ({ documentName, documentNameId })), [documentNames, selectedAgreementTypeId]);

    const { saveDisabled, saveDisabledTooltip } = useAppSelector(getDocumentQuerySaveDisabled);

    const setHistoricalDocumentId = useCallback((id: number | null) => dispatch(setSelectedHistoricalDocumentId(id)), [dispatch]);
    const agreementTypeSystemIdInfo = useAppSelector(getAgreementTypeSystemInfo);

    const isMostRecentQuery = useMemo(() => documentQueryHistory.length > 0 ? (selectedHistoricalDocumentQueryId === documentQueryHistory[documentQueryHistory.length - 1].documentQueryId) : false, [documentQueryHistory, selectedHistoricalDocumentQueryId]);
    const isEditing = useMemo(() => isMostRecentQuery && editing, [editing, isMostRecentQuery]);

    const updateQuery = useCallback((queryOrder: number, key: keyof DocumentQuery, value: string | number | number[] | FieldValue) => dispatch(updateDocumentQuery(queryOrder, key, value)), [dispatch]);
    const updateQuerySystemData = useCallback((queryOrder: number, value: SystemIdInfoUI) => dispatch(updateQuerySystemInfo(queryOrder, value)), [dispatch]);

    const selectedDocumentQuery = useMemo(() => documentQueryHistory.find(({ documentQueryId }) => documentQueryId === selectedHistoricalDocumentQueryId), [documentQueryHistory, selectedHistoricalDocumentQueryId]);
    const agreementTypeSystemIdOptions = useMemo(() => agreementTypeSystemIdInfo.map(({ systemId }) => ({ value: systemId, label: systemId })), [agreementTypeSystemIdInfo]);

    const getDefaultValueElement = useCallback((queryOrder: number, defaultAnswer: FieldValue, fieldType: SingleDatasetFieldType | null, dropdownListId: number | null, isMultiSelect: number, isEdgeCase: number) => {
        if (isNull(fieldType) || ![DatasetFieldType.DROPDOWN, DatasetFieldType.TEXT, DatasetFieldType.LONGTEXT, DatasetFieldType.NUMBER].includes(fieldType) || isEdgeCase === 1) {
            return null;
        }
        return (
            <div className={styles.aiDataPointWrapper}>
                <div className={styles.aiDataPointLabel}>Default Value</div>
                <DefaultValue defaultAnswer={defaultAnswer} updateQuery={value => updateQuery(queryOrder, 'defaultAnswer', value)} fieldType={fieldType} dropdownListId={dropdownListId} isMultiSelect={isMultiSelect} isEditing={isEditing} />
            </div>
        );
    }, [updateQuery, isEditing]);

    const updateSystemId = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null, queryOrder: number) => {
        let value = defaultSystemIdInfo;
        if (!isNull(dropdownValue)) {
            const systemId = (dropdownValue as DropdownOption).value;
            const foundSystemInfo = agreementTypeSystemIdInfo.find(systemIdInfo => systemIdInfo.systemId === systemId);
            if (foundSystemInfo) {
                value = foundSystemInfo;
            }
        }
        updateQuerySystemData(queryOrder, value);
    }, [updateQuerySystemData, agreementTypeSystemIdInfo]);

    const updateDisplayQuestion = useCallback((queryOrder: number, value: string | null) => dispatch(updateDocumentQuery(queryOrder, 'displayQuestion', value || null)), [dispatch]);

    const updateDocumentNameIds = useCallback((queryOrder: number, documentNameId: number, documentNameIds: number[]) => {
        const alreadyExists = documentNameIds.includes(documentNameId);
        const updatedDocumentNameIds = alreadyExists ? documentNameIds.filter(id => id !== documentNameId) : [...documentNameIds, documentNameId];
        dispatch(updateDocumentQuery(queryOrder, 'documentNameIds', updatedDocumentNameIds));
    }, [dispatch]);

    const [screenWidth, screenHeight] = useWindowResize();

    const wrapperHeight = useMemo(() => screenHeight - 227, [screenHeight]);

    const pieData: PortfolioTotal | undefined = useMemo(() => {
        if (selectedDocumentQuery) {
            return {
                totalQueries: selectedDocumentQuery.totalQueries,
                correctQueries: selectedDocumentQuery.correctQueries,
                percentage: selectedDocumentQuery.percentage
            };
        }
    }, [selectedDocumentQuery]);

    const currentQuery = useMemo(() => currentQueries.find(({ documentQueryId }) => documentQueryId === selectedHistoricalDocumentQueryId), [selectedHistoricalDocumentQueryId, currentQueries]);

    const resetHistoricalData = useCallback(() => dispatch(resetHistoricalQueryData()), [dispatch]);

    const saveQueries = useCallback(() => {
        dispatch(saveUpdatedQueriesStarted());
        setEditing(false);
    }, [dispatch, setEditing]);

    const handleSetIsEditing = () => {
        setEditing(!editing);
        resetHistoricalData();
    };

    const goBack = () => {
        setHistoricalDocumentId(null);
        resetHistoricalData();
    };

    const queryKeyAlreadyExists = useCallback((queryOrder: number, queryKey: string) => currentQueries.filter(query => query.queryOrder !== queryOrder).map(({ queryKey }) => queryKey).includes(queryKey), [currentQueries]);

    if (loading) {
        return <LoadingDots />;
    }

    return (
        <div className={styles.aiManagerWrapper}>
            <div className={styles.aiManagerTitleWrapper}>
                <div className={styles.aiManagerTitle} data-testid='ai-document-query-history-title'>Document Query History</div>
            </div>
            <HistoricalAIQueriesTimeline
                timeline={documentQueryHistory}
                selectedId={selectedHistoricalDocumentQueryId}
                timelineDisabled={isEditing}
            />
            <div className={styles.aiManagerContentWrapper}>
                <div className={styles.aiPieChartWrapper} style={{ height: wrapperHeight, width: `calc(100% * ${0.3} - 10px)` }}>
                    <AIManagerPieChart
                        screenHeight={screenHeight}
                        screenWidth={screenWidth}
                        selectedAgreement={undefined}
                        newAgreementSelected={false}
                        pieChartMultiplier={0.3}
                        historicalPortfolioData={pieData}
                    />
                </div>
                {isMostRecentQuery && currentQuery ? <div className={styles.aiQueries}>
                    <Scrollable>
                        <AIQueryItem
                            query={currentQuery}
                            isEditing={isEditing}
                            agreementTypeSystemIdOptions={agreementTypeSystemIdOptions}
                            relevantDocumentNames={relevantDocumentNames}
                            updateQuery={updateQuery}
                            updateDisplayQuestion={updateDisplayQuestion}
                            updateSystemId={updateSystemId}
                            getDefaultValueElement={getDefaultValueElement}
                            updateDocumentNameIds={updateDocumentNameIds}
                            queryKeyAlreadyExists={queryKeyAlreadyExists}
                            testId='historical-ai-query'
                        />
                    </Scrollable>
                    <div className={styles.aiQueriesButtons}>
                        <div className={styles.leftButtons}>
                            <Button label='Back' onClick={goBack} />
                            <Button label={isEditing ? 'Cancel' : 'Edit'} onClick={handleSetIsEditing} />
                        </div>
                        {isEditing && <div className={styles.rightButtons}>
                            <Button label='Save' onClick={saveQueries} disabled={saveDisabled} disabledTooltip={saveDisabledTooltip} />
                        </div>}
                    </div>
                </div>
                    : <div className={styles.aiQueries}>
                        <Scrollable>
                            <HistoricalAIQueryReadOnly
                                historicalQuery={selectedDocumentQuery!}
                                relevantDocumentNames={relevantDocumentNames}
                            />
                        </Scrollable>
                        <div className={styles.aiQueriesButtons}>
                            <div className={styles.leftButtons}>
                                <Button label='Back' onClick={goBack} />
                            </div>
                        </div>
                    </div>
                }
            </div>
        </div>
    );
};
